import React, { useId } from "react";

import { useController, useFormContext } from "react-hook-form";

import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FieldTitle,
} from "@/src/components/ui/field";
import { Switch } from "@/src/components/ui/switch";

import type { JSX } from "react";
import type { FieldPathByValue, FieldValues } from "react-hook-form";

interface SwitchFieldBaseProps {
  label: string;
  description?: string;
  checked: boolean;
  disabled?: boolean;
  variant?: "card" | "inline";
  onCheckedChange: (checked: boolean) => void;
}

export function SwitchFieldBase({
  label,
  description,
  checked,
  disabled,
  variant = "card",
  onCheckedChange,
}: SwitchFieldBaseProps): JSX.Element {
  const id = useId();
  const switchElement = (
    <Switch
      aria-label={label}
      checked={checked}
      disabled={disabled}
      id={id}
      onCheckedChange={onCheckedChange}
    />
  );

  if (variant === "inline") {
    return (
      <Field orientation="horizontal">
        {switchElement}
        <FieldContent>
          <FieldLabel htmlFor={id}>{label}</FieldLabel>
          {description ? (
            <FieldDescription>{description}</FieldDescription>
          ) : null}
        </FieldContent>
      </Field>
    );
  }

  return (
    <FieldLabel htmlFor={id}>
      <Field orientation="horizontal">
        <FieldContent>
          <FieldTitle>{label}</FieldTitle>
          {description ? (
            <FieldDescription>{description}</FieldDescription>
          ) : null}
        </FieldContent>
        {switchElement}
      </Field>
    </FieldLabel>
  );
}

interface SwitchFieldProps<T extends FieldValues> {
  name: FieldPathByValue<T, boolean | undefined>;
  label: string;
  description?: string;
  disabled?: boolean;
}

export function SwitchField<T extends FieldValues>({
  name,
  label,
  description,
  disabled,
}: SwitchFieldProps<T>): JSX.Element {
  const { control } = useFormContext();
  const { field } = useController({ control, name });

  return (
    <SwitchFieldBase
      checked={field.value ?? false}
      description={description}
      disabled={disabled}
      label={label}
      onCheckedChange={field.onChange}
    />
  );
}
